/**
 * FunnelVisual — W48 Marketing Dashboard Component (A)
 *
 * Horizontal SVG funnel showing MQL / SQL / Opp / Won counts with conversion
 * arrows tinted teal #0F766E. No Recharts. Custom SVG per W46 locked choice.
 *
 * Stage colors (funnel canon):
 *   MQL  — Yellow  #EAB308
 *   SQL  — Orange  #EA580C
 *   Opp  — Green   #16A34A
 *   Won  — deep-green #15803D
 *
 * Em-dash displayed where marketing_events.account_id link is missing.
 * Zero-data state handled gracefully.
 */

import { useEffect, useState } from "react";
import { getSession } from "@/lib/session";

interface FunnelData {
  mql: number;
  sql: number;
  opp: number;
  won: number;
  mqlToSqlPct: number | null;
  sqlToOppPct: number | null;
  oppToWonPct: number | null;
  quarterRange?: { start: string; end: string };
  dataNote?: string;
}

const STAGES = [
  { key: "mql", label: "MQL", color: "#EAB308", textColor: "#713F12" },
  { key: "sql", label: "SQL", color: "#EA580C", textColor: "#FFFFFF" },
  { key: "opp", label: "Opp",  color: "#16A34A", textColor: "#FFFFFF" },
  { key: "won", label: "Won",  color: "#15803D", textColor: "#FFFFFF" },
] as const;

const TEAL = "#0F766E";

function Skeleton() {
  return (
    <div className="animate-pulse space-y-3">
      <div className="h-24 rounded bg-[hsl(var(--surface-2))]" />
      <div className="h-4 w-48 rounded bg-[hsl(var(--surface-2))]" />
    </div>
  );
}

function ConversionArrow({ pct }: { pct: number | null }) {
  const label = pct === null ? "—" : `${pct}%`;
  return (
    <div className="flex flex-col items-center justify-center gap-1 px-1 select-none" style={{ minWidth: 40 }}>
      <svg width="32" height="16" viewBox="0 0 32 16" fill="none" aria-hidden="true">
        <line x1="0" y1="8" x2="26" y2="8" stroke={TEAL} strokeWidth="1.5" />
        <polygon points="24,4 32,8 24,12" fill={TEAL} />
      </svg>
      <span
        className="text-[10px] font-semibold tabular-nums"
        style={{ color: TEAL }}
      >
        {label}
      </span>
    </div>
  );
}

export function FunnelVisual() {
  const session = getSession();
  const [data, setData] = useState<FunnelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = session?.token;
    if (!token) { setError("Not authenticated"); setLoading(false); return; }

    fetch("/api/dashboards/marketing/funnel", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => {
        console.error("[FunnelVisual]", e);
        setError("Could not load funnel data");
        setLoading(false);
      });
  }, [session?.token]);

  if (loading) return <Skeleton />;
  if (error) return <p className="text-[12px] text-[#DC2626]">{error}</p>;

  const counts: Record<string, number> = {
    mql: data?.mql ?? 0,
    sql: data?.sql ?? 0,
    opp: data?.opp ?? 0,
    won: data?.won ?? 0,
  };

  const conversions: [number | null, number | null, number | null] = [
    data?.mqlToSqlPct ?? null,
    data?.sqlToOppPct ?? null,
    data?.oppToWonPct ?? null,
  ];

  const maxCount = Math.max(...Object.values(counts), 1);

  // SVG funnel bars: trapezoid shape using varying heights
  const BAR_MAX_H = 72;
  const BAR_MIN_H = 32;

  return (
    <div className="flex flex-col gap-4">
      {/* Funnel SVG row */}
      <div className="flex items-end gap-0 overflow-x-auto pb-1">
        {STAGES.map((stage, i) => {
          const count = counts[stage.key];
          const pct = maxCount > 0 ? count / maxCount : 0;
          const barH = Math.max(BAR_MIN_H, Math.round(BAR_MAX_H * (0.4 + 0.6 * pct)));
          const isLast = i === STAGES.length - 1;

          return (
            <div key={stage.key} className="flex items-end">
              {/* Stage block */}
              <div className="flex flex-col items-center gap-1.5" style={{ minWidth: 72 }}>
                {/* Count label above bar */}
                <span
                  className="text-[15px] font-bold tabular-nums"
                  style={{ color: stage.color }}
                >
                  {count}
                </span>
                {/* Trapezoid SVG bar */}
                <svg
                  width="64"
                  height={barH}
                  viewBox={`0 0 64 ${barH}`}
                  fill="none"
                  aria-label={`${stage.label}: ${count}`}
                >
                  {/* Trapezoid: wider at left, tapers right for funnel effect */}
                  <polygon
                    points={`0,0 64,${Math.round(barH * 0.15)} 64,${barH - Math.round(barH * 0.15)} 0,${barH}`}
                    fill={stage.color}
                    fillOpacity="0.9"
                  />
                  <text
                    x="32"
                    y={barH / 2 + 5}
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="700"
                    fill={stage.textColor}
                    fontFamily="Inter, system-ui, sans-serif"
                  >
                    {stage.label}
                  </text>
                </svg>
              </div>
              {/* Conversion arrow between stages */}
              {!isLast && (
                <ConversionArrow pct={conversions[i] ?? null} />
              )}
            </div>
          );
        })}
      </div>

      {/* Quarter range label */}
      {data?.quarterRange && (
        <p className="text-[11px] text-muted-foreground">
          Current quarter: {data.quarterRange.start} to {data.quarterRange.end}
        </p>
      )}

      {/* Data note for em-dash items */}
      {data?.dataNote && (
        <p className="text-[10px] text-muted-foreground italic">{data.dataNote}</p>
      )}

      {/* Legend row */}
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {STAGES.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5 text-[11px]">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-muted-foreground font-medium">{s.label}</span>
            <span className="text-foreground tabular-nums font-semibold">
              {counts[s.key]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
